import { Controller } from "@hotwired/stimulus"
import { calculate } from "../modules/salary_lab/calculator_api"
import { formatCurrency, formatPercent, parseNumber } from "../modules/salary_lab/formatter"
import { readFromUrl, writeToUrl, buildQueryString } from "../modules/salary_lab/url_state"
import { getPreference, setPreference, saveRecentCalculation } from "../modules/salary_lab/storage"
import { SmartDebounce, detectEventType } from "../modules/salary_lab/smart_debounce"
import { trackCalculatorUsed } from "../modules/salary_lab/ga4_tracker"

/**
 * Stimulus controller for the SalaryLab PAYE calculator.
 * Provides real-time AJAX calculation with debounced input,
 * URL state management, and progressive disclosure.
 */
export default class extends Controller {
  static targets = [
    "salaryInput", "frequencySelect", "hoursField", "hoursInput",
    "kiwisaverSelect", "studentLoanCheckbox", "secondaryTaxSelect", "periodSelect",
    "advancedOptions", "advancedToggle",
    "resultsPanel", "loadingIndicator",
    "netSalary", "effectiveRate", "marginalRate",
    "annualNet", "monthlyNet", "fortnightlyNet", "weeklyNet", "dailyNet", "hourlyNet",
    "annualGross", "monthlyGross", "fortnightlyGross", "weeklyGross", "dailyGross", "hourlyGross",
    "taxTotal", "accLevy", "kiwisaverEmployee", "kiwisaverEmployer",
    "studentLoanAmount", "studentLoanRow", "kiwisaverRow", "kiwisaverEmployerRow",
    "ietcAmount", "ietcRow",
    "taxBreakdown",
    "liveRegion",
    "breakdownLink"
  ]

  static values = {
    debounce: { type: Number, default: 500 },
    updateUrl: { type: Boolean, default: true },
    breakdownPath: { type: String, default: "" }
  }

  connect() {
    this._debounce = new SmartDebounce(() => this._performCalculation(), { minDigits: 4 })
    this._lastParams = null
    this._restoreState()
  }

  disconnect() {
    this._debounce.cancel()
  }

  // --- Actions ---

  inputChanged(event) {
    this._showHoursField()
    this._debounce.schedule(detectEventType(event), this.salaryInputTarget.value)
  }

  frequencyChanged() {
    this._showHoursField()
    this._debounce.schedule("toggle", this.salaryInputTarget.value)
  }

  optionChanged() {
    this._debounce.schedule("toggle", this.salaryInputTarget.value)
  }

  toggleAdvanced(event) {
    event.preventDefault()
    const visible = this.advancedOptionsTarget.classList.toggle("hidden")
    setPreference("show_advanced", !visible)
    this.advancedToggleTarget.textContent = visible ? "More options" : "Fewer options"
  }

  async submitForm(event) {
    event.preventDefault()
    this._debounce.schedule("submit")
  }

  // --- Private ---

  _restoreState() {
    const urlParams = readFromUrl()
    if (urlParams.gross_salary) {
      this._applyParams(urlParams)
      if (this.element.dataset.prerendered !== "true") {
        this._performCalculation()
      } else {
        this._dispatchPrerenderedData()
      }
    }
    if (getPreference("show_advanced", false) && this.hasAdvancedOptionsTarget) {
      this.advancedOptionsTarget.classList.remove("hidden")
      if (this.hasAdvancedToggleTarget) {
        this.advancedToggleTarget.textContent = "Fewer options"
      }
    }
    this._showHoursField()
  }

  _applyParams(params) {
    if (params.gross_salary && this.hasSalaryInputTarget) {
      this.salaryInputTarget.value = params.gross_salary
    }
    if (params.input_frequency && this.hasFrequencySelectTarget) {
      this.frequencySelectTarget.value = params.input_frequency
    }
    if (params.hours_per_week && this.hasHoursInputTarget) {
      this.hoursInputTarget.value = params.hours_per_week
    }
    if (params.kiwisaver_perc != null && this.hasKiwisaverSelectTarget) {
      this.kiwisaverSelectTarget.value = params.kiwisaver_perc
    }
    if (params.include_student_loan && this.hasStudentLoanCheckboxTarget) {
      this.studentLoanCheckboxTarget.checked = true
    }
    if (params.secondary_tax_code && this.hasSecondaryTaxSelectTarget) {
      this.secondaryTaxSelectTarget.value = params.secondary_tax_code
    }
    if (params.period_start_date && this.hasPeriodSelectTarget) {
      this.periodSelectTarget.value = params.period_start_date
    }
  }

  _showHoursField() {
    if (!this.hasHoursFieldTarget || !this.hasFrequencySelectTarget) return
    const isHourly = this.frequencySelectTarget.value === "hourly"
    this.hoursFieldTarget.classList.toggle("hidden", !isHourly)
  }

  async _performCalculation() {
    const params = this._collectParams()
    if (!params.gross_salary || params.gross_salary <= 0) return

    const paramsKey = JSON.stringify(params)
    if (paramsKey === this._lastParams) return
    this._lastParams = paramsKey

    this._showLoading(true)

    try {
      const result = await calculate(params)
      this._renderResult(result)
      if (this.updateUrlValue) writeToUrl(params)
      this._updateBreakdownLink(params)
      saveRecentCalculation(params, result)
      this._announceResult(result)
      trackCalculatorUsed("paye", { salaryAmount: params.gross_salary, payFrequency: params.input_frequency })
    } catch (error) {
      if (this.hasLiveRegionTarget) {
        this.liveRegionTarget.textContent = error.message
      }
    } finally {
      this._showLoading(false)
    }
  }

  _collectParams() {
    return {
      gross_salary: parseNumber(this.hasSalaryInputTarget ? this.salaryInputTarget.value : "0"),
      input_frequency: this.hasFrequencySelectTarget ? this.frequencySelectTarget.value : "annual",
      hours_per_week: this.hasHoursInputTarget ? parseFloat(this.hoursInputTarget.value) || 40 : 40,
      kiwisaver_perc: this.hasKiwisaverSelectTarget ? parseFloat(this.kiwisaverSelectTarget.value) || 0 : 0,
      include_student_loan: this.hasStudentLoanCheckboxTarget ? this.studentLoanCheckboxTarget.checked : false,
      secondary_tax_code: this.hasSecondaryTaxSelectTarget ? this.secondaryTaxSelectTarget.value : "",
      ...(this.hasPeriodSelectTarget && this.periodSelectTarget.value
        ? { period_start_date: this.periodSelectTarget.value }
        : {}),
    }
  }

  _showLoading(show) {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.toggle("hidden", !show)
    }
  }

  _renderResult(result) {
    if (this.hasResultsPanelTarget) {
      this.resultsPanelTarget.classList.remove("hidden")
    }

    this._setText("netSalary", formatCurrency(result.net_salary))
    this._setText("effectiveRate", formatPercent(result.effective_tax_rate))
    this._setText("marginalRate", formatPercent(result.marginal_tax_rate, 0))

    // Frequency table
    const freq = result.frequencies || {}
    this._setFrequency("annual", freq.annual)
    this._setFrequency("monthly", freq.monthly)
    this._setFrequency("fortnightly", freq.fortnightly)
    this._setFrequency("weekly", freq.weekly)
    this._setFrequency("daily", freq.daily)
    this._setFrequency("hourly", freq.hourly)

    // Deduction summary
    this._setText("taxTotal", formatCurrency(result.total_tax))
    this._setText("accLevy", formatCurrency(result.acc_levy))

    this._setDeductionRow("kiwisaverRow", "kiwisaverEmployee", result.kiwisaver_employee)
    this._setDeductionRow("kiwisaverEmployerRow", "kiwisaverEmployer", result.kiwisaver_employer)
    this._setDeductionRow("studentLoanRow", "studentLoanAmount", result.student_loan)
    this._setDeductionRow("ietcRow", "ietcAmount", result.ietc, true)

    this._renderTaxBreakdown(result.tax_breakdown)

    // Dispatch event for chart controller to pick up
    this.dispatch("calculated", { detail: result })
  }

  _setFrequency(name, data) {
    if (!data) return
    const netTarget = `${name}Net`
    const grossTarget = `${name}Gross`
    this._setText(netTarget, formatCurrency(data.net, name === "hourly" || name === "daily"))
    this._setText(grossTarget, formatCurrency(data.gross, name === "hourly" || name === "daily"))
  }

  _setDeductionRow(rowTarget, valueTarget, amount, isCredit = false) {
    const hasRow = this[`has${rowTarget.charAt(0).toUpperCase() + rowTarget.slice(1)}Target`]
    if (hasRow) {
      const show = isCredit ? amount > 0 : amount > 0
      this[`${rowTarget}Target`].classList.toggle("hidden", !show)
    }
    if (amount > 0) {
      const prefix = isCredit ? "-" : ""
      this._setText(valueTarget, `${prefix}${formatCurrency(amount)}`)
    }
  }

  _renderTaxBreakdown(breakdown) {
    if (!this.hasTaxBreakdownTarget || !breakdown) return

    const rows = breakdown.map(bracket => {
      const rate = bracket.rate != null ? `${bracket.rate}%` : bracket.description
      return `<tr>
        <td class="py-1 text-sm text-gray-600">${rate}</td>
        <td class="py-1 text-sm tabular-nums text-right">${formatCurrency(bracket.attributable_income)}</td>
        <td class="py-1 text-sm tabular-nums text-right font-medium">${formatCurrency(bracket.tax_component)}</td>
      </tr>`
    }).join("")

    this.taxBreakdownTarget.innerHTML = rows
  }

  _updateBreakdownLink(params) {
    if (!this.hasBreakdownLinkTarget || !this.breakdownPathValue) return
    const qs = buildQueryString(params)
    this.breakdownLinkTarget.href = qs
      ? `${this.breakdownPathValue}?${qs}`
      : this.breakdownPathValue
  }

  _setText(targetName, text) {
    const capitalized = targetName.charAt(0).toUpperCase() + targetName.slice(1)
    if (this[`has${capitalized}Target`]) {
      this[`${targetName}Target`].textContent = text
    }
  }

  _announceResult(result) {
    if (!this.hasLiveRegionTarget) return
    this.liveRegionTarget.textContent =
      `Your take-home pay is ${formatCurrency(result.net_salary)} per year, ` +
      `${formatCurrency(result.frequencies?.fortnightly?.net)} per fortnight. ` +
      `Effective tax rate ${formatPercent(result.effective_tax_rate)}.`
  }

  _dispatchPrerenderedData() {
    const json = this.element.dataset.prerenderedResults
    if (!json) return
    try {
      const result = JSON.parse(json)
      requestAnimationFrame(() => this._renderResult(result))
    } catch { /* ignore parse errors */ }
  }
}
